// The waivers beside the lineup (B-316, DIRECTION U21): when claims run, my
// budget or priority, my claims in the order they are tried — with their
// arrows, Edit and Withdraw — and who is on waivers, with Claim. The log, the
// order of every team and the commissioner's tools stay on the Waivers screen,
// a tap away at the panel's head.

import { Fragment } from 'react';
import type { ClaimView, WaiversView, WaiverPlayerRow } from '@fsp/domain';
import { useReaderZone, useWaivers } from '../../data/hooks';
import { useAtLeast } from '../../data/detail';
import { EmptyState, GameLabel, PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import { Button, Caption, IconButton } from '../../kit/ui';
import { FromLeague } from '../shared/FromLeague';
import { PlayerName } from '../shared/Names';
import { num } from '../shared/notComputed';
import { Proj } from '../shared/Projection';
import { fmtWhen, ordinal, type RosterMove } from '../shared/RosterMoveSheet';
import type { PlayerCardData } from '../shared/PlayerCard';
import { useWaiverWrites } from '../shared/waiverWrites';
import '../../styles/screens/waivers.css';
import { Term } from '../../kit/Why';
import { useTools } from '../../data/tools';

export function WaiversPanel({
  onOpen,
  onMove,
}: {
  onOpen: (card: PlayerCardData) => void;
  onMove: (move: RosterMove) => void;
}) {
  return (
    <FromLeague snapshot={useWaivers()}>
      {(view) => <WaiversPanelBody view={view} onOpen={onOpen} onMove={onMove} />}
    </FromLeague>
  );
}

function WaiversPanelBody({
  view,
  onOpen,
  onMove,
}: {
  view: WaiversView;
  onOpen: (card: PlayerCardData) => void;
  onMove: (move: RosterMove) => void;
}) {
  const zone = useReaderZone() ?? view.timeZone;
  // A projection is a Standard number on Waivers (DETAIL_LEVELS §Waivers).
  // None in a league that keeps no projections (RULES_SCHEMA §15c).
  const standard = useAtLeast('standard');
  const tools = useTools();
  const showProjection = standard && tools.projections;
  const { busy, moveClaim, withdraw } = useWaiverWrites(view);

  const openClaim = (c: ClaimView) =>
    onOpen({
      player: c.player,
      game: c.game,
      projection: c.projection,
      points: null,
      statLine: null,
      action: null,
    });
  const openWire = (r: WaiverPlayerRow) => {
    const kind = r.action === 'none' ? null : r.action;
    onOpen({
      player: r.player,
      game: r.game,
      projection: r.projection,
      points: null,
      statLine: null,
      action: kind
        ? {
            label: r.myClaimId ? 'Edit claim' : kind === 'add' ? 'Add' : 'Claim',
            onClick: () => onMove({ kind, playerId: r.player.id, claimId: r.myClaimId }),
          }
        : null,
    });
  };

  return (
    <div className="fsp-side-list">
      <div className="fsp-side-intro">
        {/* The rule says when claims run ("Processes Wednesday at 3:00 AM"),
            and each player on the wire says when he clears. */}
        <div className="fsp-sub fsp-ui-wrap-text">{view.headline}</div>
        {view.budget || view.priority ? (
          <div className="fsp-side-figures">
            {view.budget ? (
              <span>
                <Caption>Budget</Caption>{' '}
                <span className="fsp-num">
                  ${view.budget.left}
                  <span className="fsp-muted"> of ${view.budget.total}</span>
                </span>
              </span>
            ) : null}
            {view.priority ? (
              <span>
                <Caption>Priority</Caption>{' '}
                <Term
                  tip={view.priority.why}
                  name={`${ordinal(view.priority.rank)} of ${view.priority.of}`}
                >
                  <span className="fsp-num">
                    {ordinal(view.priority.rank)}
                    <span className="fsp-muted"> of {view.priority.of}</span>
                  </span>
                </Term>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="fsp-side-section" role="heading" aria-level={3}>
        <Caption>Your claims</Caption>
      </div>
      {view.myClaims.length === 0 ? (
        <EmptyState>No claims yet.</EmptyState>
      ) : (
        view.myClaims.map((c, i) => (
          <div
            key={c.id}
            className={`fsp-row fsp-side-claim ${c.problem ? 'fsp-row-problem' : ''}`}
          >
            <Term
              className="fsp-claim-order fsp-num"
              tip={`Tried ${ordinal(c.order)} of your claims.`}
              name={String(c.order)}
            >
              {c.order}
            </Term>
            <div className="fsp-side-who">
              <div className="fsp-name">
                <button
                  type="button"
                  className="fsp-linklike fsp-name-text fsp-name-btn"
                  onClick={() => openClaim(c)}
                >
                  {c.player.name}
                </button>
                <ProTeamCode code={c.player.proTeam} />
                <StatusBadge player={c.player} />
              </div>
              <div className="fsp-sub fsp-ui-wrap-text">
                {c.bid !== null ? <span className="fsp-num">${c.bid} · </span> : null}
                {c.drops.length
                  ? [
                      'drop ',
                      ...c.drops.map((d, j) => (
                        <Fragment key={d.id}>
                          {j > 0 ? ', ' : ''}
                          <PlayerName player={d} club={d.proTeam}>
                            {d.shortName}
                          </PlayerName>
                        </Fragment>
                      )),
                    ]
                  : 'no drop'}
                {c.conditionalOn ? ` · only if ${c.conditionalOn.playerName} fails` : ''}
              </div>
              <div
                className={`fsp-sub fsp-ui-wrap-text fsp-claim-standing ${c.problem ? 'problem' : ''}`}
              >
                {c.standing}
              </div>
            </div>
            <div className="fsp-side-claim-tools">
              <IconButton
                icon="up"
                label="Try earlier"
                variant="quiet"
                size="sm"
                disabled={i === 0 || busy}
                onClick={() => moveClaim(c.id, -1)}
              />
              <IconButton
                icon="down"
                label="Try later"
                variant="quiet"
                size="sm"
                disabled={i === view.myClaims.length - 1 || busy}
                onClick={() => moveClaim(c.id, 1)}
              />
              <IconButton
                icon="edit"
                label={`Edit the claim for ${c.player.name}`}
                variant="quiet"
                size="sm"
                onClick={() => onMove({ kind: 'claim', playerId: c.player.id, claimId: c.id })}
              />
              <IconButton
                icon="withdraw"
                label={`Withdraw the claim for ${c.player.name}`}
                variant="quiet"
                size="sm"
                disabled={busy}
                onClick={() => withdraw(c)}
              />
            </div>
          </div>
        ))
      )}

      <div className="fsp-side-section" role="heading" aria-level={3}>
        <Caption>On waivers</Caption>
      </div>
      {view.onWaivers.length === 0 ? (
        <EmptyState>Nobody is on waivers right now.</EmptyState>
      ) : (
        view.onWaivers.map((r) => (
          <div
            key={r.player.id}
            className="fsp-row fsp-row-clickable fsp-side-player"
            data-ux-convenience=""
            onClick={() => openWire(r)}
          >
            <PositionPill pos={r.player.positions[0]!} size="sm" />
            <div className="fsp-side-who">
              <div className="fsp-name">
                <button
                  type="button"
                  className="fsp-linklike fsp-name-text fsp-name-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openWire(r);
                  }}
                >
                  {r.player.name}
                </button>
                <ProTeamCode code={r.player.proTeam} />
                <StatusBadge player={r.player} />
              </div>
              <div className="fsp-sub fsp-side-sub">
                <GameLabel game={r.game} />
                {r.cleared ? ' · free agent' : ` · on waivers until ${fmtWhen(r.clearsAt, zone)}`}
              </div>
            </div>
            <div className="fsp-right fsp-num fsp-side-num">
              {showProjection ? <Proj>{num(r.projection)}</Proj> : null}
            </div>
            <div data-ux-stop="" onClick={(e) => e.stopPropagation()}>
              {r.myClaimId ? (
                <Button
                  size="xs"
                  full
                  variant="secondary"
                  onClick={() =>
                    onMove({ kind: 'claim', playerId: r.player.id, claimId: r.myClaimId })
                  }
                >
                  Claimed
                </Button>
              ) : r.action !== 'none' ? (
                <Button
                  size="xs"
                  full
                  variant={r.action === 'claim' ? 'caution-soft' : 'soft'}
                  onClick={() =>
                    onMove({ kind: r.action as 'add' | 'claim', playerId: r.player.id })
                  }
                >
                  {r.action === 'claim' ? 'Claim' : 'Add'}
                </Button>
              ) : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
